import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { message } from 'antd';
import AddReward from '../../presentation/addReward/AddReward';
import { useManageRewardsService } from '../../services/manageRewards.service.jsx';
import * as htmlToImage from 'html-to-image';
import { CoreBadge } from '../../../RnRCommonComponent';
import { snappy } from 'src/_shared';
import { uploadToS3, makeStringFilenameSafe, uploadPngBase64ToS3 } from 'src/_shared/services/utils';
import { withApollo} from 'react-apollo';
import gql from 'graphql-tag';
import { v4 as uuidv4 } from 'uuid';
import { typeMap, iconMap } from 'src/manageRewards/constants/manageRewardsAPI.constants';

const QUERY_SNAPPY_GRANTS_BY_COMPANY_ID = gql`
	query QuerySnappyGrantsByCompanyId(
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		querySnappyGrantsByCompanyId(
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				name
				description
				externalSnappyId
				companyId
				snappyCampaignId
				snappyCampaignTierId
				active
				allowOverBudget
				allowMultipleGrants
				allowForNominations
				allowRevoke
				requireApproval
				allowAssignmentNoGift
				grantCategory
				costInCurrency
				currencyIsoCode
				currencySymbol
				giftExpiryDays
				remainingAssignments
				totalAssignments
				badgeIds
				badgeAssignmentIds
				snappyGiftIds
				isHidden
				isArchived
				isMirroredSnappyCampaignTier
				externalInformation
				redemptionLogs
				createdBy
				createdAt
				updatedBy
				updatedAt
			}
			nextToken
		}
	}
`;

const CREATE_BADGE = gql`
	mutation CreateBadge($input: CreateBadgeInput!) {
		createBadge(input: $input) {
			id
			assignmentLevelId
    		assignmentLimit
    		badgeTypeId
    		bonusPoints
    		color
    		companyId
    		createdBy
    		description
    		iconId
    		isPreApproved
    		label
    		name
    		points
    		recipientLevelId
    		rewardBonus
    		visibility
		}
	}
`;

const AddRewardContainer = (props) => {
	const { isModalOpen, handleCloseModal, setShouldRefresh, currentUser } = props;

	const [badgeDetailsObject, setBadgeDetailsObject] = useState({});
	const [badgeErrorObject, setBadgeErrorObject] = useState();
	const [iconsList, setIconsList] = useState([]);
	const [automationRules, setAutomationRule] = useState([]);
	const [badgeTypeList, setBadgeTypeList] = useState();
	const [isLoaderVisible, setIsLoaderVisible] = useState(false);
	const [levelList, setLevelList] = useState();
	const [payTypeList, setPayTypeList] = useState();
	const [automation, setAutomation] = useState();
	const [campaigns, setCampaigns] = useState([]);
	const manageRewardService = useManageRewardsService();

	useEffect(() => {
		getBadgeTypesList();
		getIconsList();
		getLevelList();
		getPayTypeList();
		setBadgeErrorObject(null);
		getAutomationRules();
		getCampaigns();
	}, []);

	const getCampaigns = async () => {
		try {
			const companyId = currentUser?.companyId || currentUser?.company?.id;
			if (!companyId) {
				console.error('Company ID not found');
				return;
			}

			const { data } = await props.client.query({
				query: QUERY_SNAPPY_GRANTS_BY_COMPANY_ID,
				variables: {
					companyId: companyId,
					limit: 1000,
					nextToken: null
				},
				fetchPolicy: 'network-only'
			});

			if (data?.querySnappyGrantsByCompanyId?.items) {
				setCampaigns(data.querySnappyGrantsByCompanyId.items);
			} else {
				setCampaigns([]);
			}
		} catch (error) {
			console.error('Error fetching campaigns:', error);
			setCampaigns([]);
		}
	};

	const getLevelList = () => {
		manageRewardService
			.getEmployeeLevels()
			.then((response) => {
				if (response?.code === 200) {
					if (response?.data) {
						setLevelList(response?.data);
					}
				} else {
					message.error('Failed to fetch badge type list! Try again later.');
				}
			})
			.catch((error) => {
				message.error('Failed to fetch badge type list! Try again later.');
			});
	};

	const getPayTypeList = () => {
		manageRewardService
			.getEmployeePayType()
			.then((response) => {
				if (response?.code === 200) {
					if (response?.data) {
						setPayTypeList(response?.data);
					}
				} else {
					message.error('Failed to fetch badge type list! Try again later.');
				}
			})
			.catch((error) => {
				message.error('Failed to fetch badge type list! Try again later.');
			});
	};

	const getBadgeTypesList = () => {
		manageRewardService
			.getBadgeTypes()
			.then((getBadgeTypesListRes) => {
				if (getBadgeTypesListRes?.code === 200) {
					if (getBadgeTypesListRes?.data) {
						setBadgeTypeList(getBadgeTypesListRes?.data);
					}
				} else {
					message.error('Failed to fetch badge type list! Try again later.');
				}
			})
			.catch((error) => {
				message.error('Failed to fetch badge type list! Try again later.');
			});
	};

	const getIconsList = () => {
		manageRewardService
			.getIcons()
			.then((iconsListResponse) => {
				if (iconsListResponse?.code === 200) {
					if (iconsListResponse?.data) {
						iconsListResponse?.data.sort((a, b) => {
							return a.name.localeCompare(b.name);
						});
						setIconsList(iconsListResponse?.data);
					}
				} else {
					message.error('Failed to fetch icons list! Try again later.');
				}
			})
			.catch((error) => {
				message.error('Failed to fetch icons list! Try again later.');
			});
	};

	const getAutomationRules = () => {
		manageRewardService
			.getAutomationRule()
			.then((automationRuleListResponse) => {
				if (automationRuleListResponse?.code === 200) {
					if (automationRuleListResponse?.data) {
						setAutomationRule(automationRuleListResponse?.data);
					}
				} else {
					message.error(
						'Failed to fetch Automation Rules list! Try again later.'
					);
				}
			})
			.catch(() => {
				message.error(
					'Failed to fetch Automation Rules list! Try again later.'
				);
			});
	};

	const handleCreateBadge = async () => {
		const requiredFields = ['name', 'label', 'description', 'color'];
		let hasEmptyField = false;
		let errorObject = badgeErrorObject && badgeErrorObject;

		for (const field of requiredFields) {
			if (
				!badgeDetailsObject[field] ||
				badgeDetailsObject[field]?.trim() === ''
			) {
				errorObject = {
					...errorObject,
					[field]: true,
				};

				hasEmptyField = true;
			}
		}

		const pointsValue = parseInt(badgeDetailsObject.points);
		if (
			badgeDetailsObject.points === '' ||
			isNaN(badgeDetailsObject.points) ||
			pointsValue < 0 ||
			pointsValue > 100000
		) {
			errorObject = {
				...errorObject,
				points: true,
			};
			hasEmptyField = true;
		}

		setBadgeErrorObject(errorObject);

		if (hasEmptyField) {
			return;
		}

		try {
			const badgeElement = document.getElementById('badge-preview');
			const dataUrl = await htmlToImage.toPng(badgeElement);
			setIsLoaderVisible(true);
			const badgeTypeId = badgeDetailsObject.badgeType ? typeMap[badgeDetailsObject.badgeType] : null;
			const iconId = badgeDetailsObject.iconCode ? iconMap[badgeDetailsObject.iconCode] : null;
			const rewardBonus = badgeDetailsObject.rewardBonus ? parseInt(badgeDetailsObject.rewardBonus) : 0;
			const s3Key = "BadgeTemplates" + makeStringFilenameSafe(badgeDetailsObject.name) + "-" + uuidv4();
			const s3Response = await uploadPngBase64ToS3(dataUrl, s3Key, 'rewards-recognition-dev');
			const isPreApproved = currentUser?.company?.recognitionSettings?.requireAdminApproval ? true : badgeDetailsObject?.isPreApproved;
			const image = s3Key;
			const snappyGrantId = badgeDetailsObject.snappyGrantId ? badgeDetailsObject.snappyGrantId : null;

			let payload = {
				...badgeDetailsObject,
				badgeTypeId,
				iconId,
				image,
				automationRule: automation,
				createdBy: currentUser.id,
				userId: currentUser.id,
				isPreApproved,
				rewardBonus,
				companyId: currentUser?.companyId || currentUser?.company?.id,
				bonusPoints: badgeDetailsObject.points || 0,
				assignmentLimit: badgeDetailsObject.assignmentLimit || 1,
				rewardMaxBonus: badgeDetailsObject.rewardMaxBonus || 0,
			}
			if (snappyGrantId) payload.snappyGrantId = snappyGrantId
			if (payload.badgeType) {
				delete payload.badgeType;
			}
			if (payload.iconCode) {
				delete payload.iconCode;
			}
			if (payload.snappyGrantId) {
				delete payload.snappyGrantId;
			}
			try {
				const createBadgeRes = await props.client.mutate({
					mutation: CREATE_BADGE,
					variables: {
						input: payload,
					},
				});
				if (createBadgeRes?.data?.createBadge) {
					message.success('Badge created successfully');
					setShouldRefresh(true);
					handleCloseModal();
					setIsLoaderVisible(false);
				} else {
					message.error('Failed to create badge! Try again later.');
						setIsLoaderVisible(false);
					}
			} catch (error) {
				console.error('Failed to create badge:', error);
				message.error('Failed to create badge! Try again later.');
				setIsLoaderVisible(false);
			}
		} catch (error) {
			console.error('Failed to create badge image:', error);
			message.error('Failed to create badge image! Try again later.');
		}
	};

	return (
		<div>
			<AddReward
				campaigns={campaigns}
				currentUser={currentUser}
				iconsList={iconsList}
				badgeTypeList={badgeTypeList}
				isModalOpen={isModalOpen}
				handleCloseModal={handleCloseModal}
				handleCreateBadge={handleCreateBadge}
				badgeDetailsObject={badgeDetailsObject}
				setBadgeDetailsObject={setBadgeDetailsObject}
				badgeErrorObject={badgeErrorObject}
				setBadgeErrorObject={setBadgeErrorObject}
				isLoaderVisible={isLoaderVisible}
				levelList={levelList}
				payTypeList={payTypeList}
				automation={automation}
				setAutomation={setAutomation}
				automationRules={automationRules}
			/>
		</div>
	);
};

export default withApollo(AddRewardContainer);
